import { useMemo } from 'react';

/**
 * Simple hook to organize events into top-level items
 * Each component is responsible for rendering its own children
 */
export function useSimpleEventOrganization(allEvents) {
  return useMemo(() => {
    // Find open assignment (always at top)
    const openAssignment = allEvents.find(e => e.eventType === 'assignment' && e.status === 'open');

    // Build a set of all assignment IDs that exist in our loaded events
    const loadedAssignmentIds = new Set(
      allEvents
        .filter(e => e.eventType === 'assignment')
        .map(e => e.eventId)
    );

    // Get all events that are NOT children of anything
    const topLevelEvents = allEvents.filter(e => {
      // If event belongs to an assignment, only filter it out if the parent assignment is loaded
      if (e.metadata?.assignmentEventId) {
        const parentIsLoaded = loadedAssignmentIds.has(e.metadata.assignmentEventId);
        if (parentIsLoaded) {
          return false; // Parent exists, filter out
        } else {
          // Parent not loaded - treat as top-level event (orphaned)
          return true;
        }
      }

      // Skip individual matches that belong to groups
      if (e.eventType === 'match' && e.metadata?.groupEventId) {
        return false;
      }

      // Skip the open assignment itself (we'll add it manually at top)
      if (e.eventId === openAssignment?.eventId) {
        return false;
      }

      return true;
    });

    // Sort by time (newest first)
    topLevelEvents.sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());

    // Return open assignment first, then other top-level events
    return openAssignment ? [openAssignment, ...topLevelEvents] : topLevelEvents;
  }, [allEvents]);
}
